import { Component, inject, Injector, input, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ComponentStore } from '@ngrx/component-store';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { IDatatableColumn, IDatatableItem, itemsDatatableColumns } from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { IDropdownOption } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ConfigurationDialogManager } from '../../../../../shared/components/page-configuration/configuration-dialog-manager';
import {
  IPageConfigurationInputs,
  PageConfigurationComponent,
} from '../../../../../shared/components/page-configuration/page-configuration.component';
import { IPageConfigurationData } from '../../../../../shared/components/page-configuration/page-configuration.interface';

export interface IPageConfigurationBuilderParams {
  availableHeaders: [IDropdownOption[], IDropdownOption[]];
  initiallySelectedHeaders: number[];
}

export interface IDatatableConfigurationContext<TStore extends ComponentStore<any> = ComponentStore<any>> {
  availableStandardHeaders: (Omit<IDatatableColumn<IDatatableItem>, 'id' | 'name'> & {
    id: number;
    name: string;
  })[];
  defaultHeaders: number[];
  store: TStore;
}

@Component({
  imports: [MatButton, TranslatePipe, MatIcon],
  selector: 'datatable-configuration-input',
  styleUrl: './datatable-configuration-input.component.scss',
  templateUrl: './datatable-configuration-input.component.html',
})
export class DatatableConfigurationInputComponent {
  public readonly formControl = input.required<FormControl<number[]>>();
  public readonly store = input.required<{
    customFields$: Observable<IDropdownOption[]>;
    loadCustomFields: () => Subscription;
  }>();
  public readonly availableStandardHeaders = input.required<typeof itemsDatatableColumns>();
  public readonly defaultHeaders = input.required<number[]>();
  public readonly pageConfigurationBuilder =
    input.required<(params: IPageConfigurationBuilderParams) => IPageConfigurationData>();

  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);

  private readonly manager = new ConfigurationDialogManager();

  private readonly selectedHeaders$ = this.manager.onApply.pipe(
    map((applyData): number[] => {
      const apply = (applyData?.configurations.find(({ type }) => type === 'datatable')?.ids ?? null) as
        | number[]
        | null;

      if (!this.formControl().value?.length) {
        this.formControl().reset(this.defaultHeaders());
      }

      return apply ?? this.formControl().value ?? [];
    }),
  );

  public ngOnInit(): void {
    this.store().loadCustomFields();

    runInInjectionContext(this.injector, () => {
      this.getPageConfigurationUpdateSubscription();
      this.setFormControlOnApply();
    });
  }

  public openDialog(): void {
    this.dialog.open(PageConfigurationComponent, {
      data: {
        dialogTitle: 'button.configureColumns',
        manager: this.manager,
      } satisfies IPageConfigurationInputs,
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });
  }

  private getPageConfigurationUpdateSubscription(): Subscription {
    return combineLatest({
      customFields: this.store().customFields$,
      initiallySelectedHeaders: this.selectedHeaders$,
    })
      .pipe(takeUntilDestroyed())
      .subscribe(({ customFields, initiallySelectedHeaders }) => {
        this.manager.isPageConfigurationDisabledSubject.next(false);
        this.manager.configurationsSubject.next(
          this.pageConfigurationBuilder()({
            availableHeaders: [this.availableStandardHeaders(), customFields],
            initiallySelectedHeaders,
          }),
        );
      });
  }

  private setFormControlOnApply(): void {
    this.selectedHeaders$.pipe(takeUntilDestroyed()).subscribe((headers) => {
      this.formControl().reset(headers);
    });
  }
}
